/**
 * Monthly Attendance Approval — HR locks the month's attendance before payroll.
 *
 * Sections:
 *   1. Header — month picker, working-days override, lock/unlock buttons, status banner
 *   2. Weekly Off Calendar + Holiday Calendar — separate click-to-toggle month grids
 *   3. Late-mark policy — every N late marks = 1 day deducted
 *   4. Employee snapshot — filters (department + salary_type), inline-edit per row,
 *      live payable_days calculation
 */
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from './ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from './ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from './ui/dialog';
import {
  Lock, Unlock, ShieldCheck, Loader2, AlertTriangle, Users,
  CalendarDays, Pencil, FileText, Filter, X, Check
} from 'lucide-react';
import { toast } from 'sonner';
import { API_BASE as API_URL } from '../lib/apiBase';
import { safeJson } from '../lib/safeJson';
import { MonthTallySummary } from './MonthTallySummary';
import { WeeklyOffPolicyCard } from './WeeklyOffPolicyCard';
import { PayableTallyPanel } from './PayableTallyPanel';
import { WeeklyOffCalendar } from './WeeklyOffCalendar';

const headers = () => {
  const t = localStorage.getItem('hrms-token');
  return t ? { 'Authorization': `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
};

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

// ---------------- Holiday Calendar ----------------
function HolidayCalendar({ entityId, month, holidays, weekOffDates = [], onChange, disabled }) {
  // Build a list of date cells for the chosen month
  const [busy, setBusy] = useState(false);
  const cells = useMemo(() => {
    const [y, m] = month.split('-').map(Number);
    const first = new Date(y, m - 1, 1);
    const totalDays = new Date(y, m, 0).getDate();
    const startDayOfWeek = first.getDay(); // 0=Sun
    const arr = [];
    for (let i = 0; i < startDayOfWeek; i++) arr.push(null);
    for (let d = 1; d <= totalDays; d++) {
      const iso = `${month}-${String(d).padStart(2, '0')}`;
      const dow = new Date(y, m - 1, d).getDay();
      arr.push({ iso, day: d, dow });
    }
    return arr;
  }, [month]);

  const holidayMap = useMemo(() => {
    const m = {};
    holidays.forEach(h => { m[h.date] = h; });
    return m;
  }, [holidays]);

  const weekOffSet = useMemo(() => new Set(weekOffDates), [weekOffDates]);

  const toggle = async (iso) => {
    if (!entityId || disabled || busy) return;
    setBusy(true);
    try {
      const existing = holidayMap[iso];
      if (existing) {
        const res = await fetch(`${API_URL}/api/attendance/holidays?entity_id=${entityId}&holiday_date=${iso}`,
          { method: 'DELETE', headers: headers(), credentials: 'include' });
        const d = await safeJson(res);
        if (!res.ok) throw new Error(d.detail || 'Failed to remove holiday');
      } else {
        const dow = new Date(iso).getDay();
        const label = dow === 0 ? 'Sunday' : 'Holiday';
        const type = dow === 0 ? 'weekly_off' : 'institute';
        const res = await fetch(`${API_URL}/api/attendance/holidays?entity_id=${entityId}&holiday_date=${iso}&label=${encodeURIComponent(label)}&holiday_type=${type}`,
          { method: 'POST', headers: headers(), credentials: 'include' });
        const d = await safeJson(res);
        if (!res.ok) throw new Error(d.detail || 'Failed to add holiday');
      }
      onChange();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <Card data-testid="holiday-calendar">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center gap-2">
          <CalendarDays className="w-4 h-4 text-[#DC2626]" />
          Holiday Calendar — click a date to declare it a PAID holiday
          {busy && <Loader2 className="w-3 h-3 animate-spin text-slate-400" />}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-7 gap-1 text-xs text-slate-500 mb-1 select-none">
          {dayNames.map(n => <div key={n} className="text-center font-semibold">{n}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {cells.map((c, i) => {
            if (!c) return <div key={`pad-${i}`} className="h-9" />;
            const h = holidayMap[c.iso];
            const isHoliday = !!h;
            const isWeekOff = weekOffSet.has(c.iso);
            return (
              <button
                key={c.iso}
                type="button"
                disabled={disabled || busy}
                onClick={() => toggle(c.iso)}
                className={`relative h-9 rounded-md border text-xs font-medium transition-all ${
                  isHoliday && h.holiday_type !== 'weekly_off'
                    ? 'bg-red-100 border-red-300 text-red-800'
                    : isWeekOff
                      ? 'bg-blue-100 border-blue-300 text-blue-800'
                      : 'bg-white border-slate-200 text-slate-700 hover:bg-amber-50 hover:border-amber-300'
                }`}
                title={isHoliday ? `${h.label} (click to remove)` : (isWeekOff ? 'Weekly off (paid)' : 'Click to mark as holiday')}
                data-testid={`cal-day-${c.iso}`}
              >
                {c.day}
                {isHoliday && (
                  <Check className="w-2.5 h-2.5 absolute top-0.5 right-0.5" />
                )}
              </button>
            );
          })}
        </div>
        <div className="mt-2 flex items-center gap-3 text-[10px] text-slate-500">
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-blue-100 border border-blue-300" /> Weekly off (paid, from policy)</span>
          <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded bg-red-100 border border-red-300" /> Declared holiday (paid)</span>
          <span className="ml-auto">{holidays.length} holiday(s) · {weekOffDates.length} weekly off(s) in {month}</span>
        </div>
      </CardContent>
    </Card>
  );
}

// ---------------- Inline-edit dialog ----------------
function EditEmployeeDialog({ row, onClose, onSave }) {
  const [present, setPresent] = useState(row?.present_days ?? 0);
  const [leaves, setLeaves] = useState(row?.approved_leave_days ?? 0);
  const [lwp, setLwp] = useState(row?.lwp_days ?? 0);
  const [late, setLate] = useState(row?.late_marks ?? 0);
  const [paidH, setPaidH] = useState(row?.paid_holidays ?? 0);
  const [weekOff, setWeekOff] = useState(row?.week_off_days ?? 0);

  useEffect(() => {
    if (row) {
      setPresent(row.present_days ?? 0);
      setLeaves(row.approved_leave_days ?? 0);
      setLwp(row.lwp_days ?? 0);
      setLate(row.late_marks ?? 0);
      setPaidH(row.paid_holidays ?? 0);
      setWeekOff(row.week_off_days ?? 0);
    }
  }, [row]);

  if (!row) return null;
  const md = Number(row.served_days ?? row.month_days ?? row.working_days ?? 30);
  const used = Number(present) + Number(leaves) + Number(lwp) + Number(paidH) + Number(weekOff);
  const absent = Math.max(0, Math.round((md - used) * 10) / 10);
  const over = used > md;

  const save = () => {
    onSave({
      employee_id: row.employee_id,
      present_days: Number(present),
      approved_leave_days: Number(leaves),
      lwp_days: Number(lwp),
      late_marks: Number(late),
      paid_holidays: Number(paidH),
      week_off_days: Number(weekOff),
      working_days: Number(row.working_days),
    });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="ma-edit-dialog">
        <DialogHeader>
          <DialogTitle>Edit attendance — {row.employee_name}</DialogTitle>
          <p className="text-xs text-slate-500">{row.designation} · {row.department}</p>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Present Days</Label>
              <Input type="number" step="0.5" min="0" max="31" value={present}
                onChange={(e) => setPresent(e.target.value)} data-testid="ma-edit-present" />
              <p className="text-[10px] text-slate-500">Half-days allowed — e.g. 24.5</p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Approved Paid Leaves</Label>
              <Input type="number" step="0.5" min="0" value={leaves}
                onChange={(e) => setLeaves(e.target.value)} data-testid="ma-edit-leaves" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Leave Without Pay</Label>
              <Input type="number" step="0.5" min="0" value={lwp}
                onChange={(e) => setLwp(e.target.value)} data-testid="ma-edit-lwp" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Holidays (paid)</Label>
              <Input type="number" min="0" max="31" value={paidH}
                onChange={(e) => setPaidH(e.target.value)} data-testid="ma-edit-paid-holidays" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Weekly Offs</Label>
              <Input type="number" min="0" max="31" value={weekOff}
                onChange={(e) => setWeekOff(e.target.value)} data-testid="ma-edit-weekoff" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Late Marks</Label>
              <Input type="number" min="0" value={late}
                onChange={(e) => setLate(e.target.value)} data-testid="ma-edit-late" />
              <p className="text-[10px] text-slate-500">Deduction applied per entity policy.</p>
            </div>
          </div>
          <div className={`rounded-md border p-2 text-xs ${over ? 'bg-red-50 border-red-300 text-red-800' : 'bg-slate-50 border-slate-200 text-slate-700'}`}
            data-testid="ma-edit-tally">
            {over ? (
              <>These numbers add up to <strong>{used}</strong>, more than the <strong>{md}</strong> days
                in this month. Reduce one of them so the total is {md} or less.</>
            ) : (
              <>{present || 0} present + {leaves || 0} leave + {lwp || 0} LWP + {paidH || 0} holiday + {weekOff || 0} weekly off
                + <strong>{absent} absent</strong> = {md} days. Absent is filled in automatically.</>
            )}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="ma-edit-cancel">Cancel</Button>
            <Button onClick={save} disabled={over} className="bg-[#DC2626] hover:bg-[#B91C1C] text-white" data-testid="ma-edit-save">
              Save
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------- Main component ----------------
export function MonthlyAttendanceApproval({ entityId }) {
  const [month, setMonth] = useState(currentMonth());
  const [workingDaysOverride, setWorkingDaysOverride] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [acting, setActing] = useState(false);
  const [holidays, setHolidays] = useState([]);
  const [lateRatio, setLateRatio] = useState(3);
  const [overrides, setOverrides] = useState({}); // employee_id -> override
  const [editingRow, setEditingRow] = useState(null);
  const [whyRow, setWhyRow] = useState(null);   // employee whose payable-day breakdown is open
  const [filterDept, setFilterDept] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const reqRef = useRef(0);

  const refresh = useCallback(async () => {
    if (!entityId) {
      toast.error('Pick an Institute / Branch in the top selector first.');
      return;
    }
    const reqId = ++reqRef.current;   // ignore responses from an older month/entity
    setLoading(true);
    try {
      const [statusRes, holRes] = await Promise.all([
        fetch(`${API_URL}/api/attendance/monthly-approval/${entityId}/${month}`,
          { headers: headers(), credentials: 'include' }),
        fetch(`${API_URL}/api/attendance/holidays?entity_id=${entityId}&month=${month}`,
          { headers: headers(), credentials: 'include' }),
      ]);
      const d = await safeJson(statusRes);
      if (reqId !== reqRef.current) return;   // a newer request started — drop this stale one
      if (!statusRes.ok) throw new Error(d.detail || 'Failed to load monthly approval status');
      setData(d);
      setLateRatio(d.late_to_day_ratio || 3);
      const hols = await safeJson(holRes);
      if (reqId !== reqRef.current) return;
      setHolidays(Array.isArray(hols) ? hols : []);
      // NOTE: pending overrides are intentionally KEPT across refreshes (e.g. after toggling
      // a holiday) — they are only cleared when the month/entity changes or after locking.
    } catch (e) {
      if (reqId === reqRef.current) toast.error(e.message);
    } finally {
      if (reqId === reqRef.current) setLoading(false);
    }
  }, [entityId, month]);

  useEffect(() => {
    if (entityId) refresh();
  }, [entityId, month, refresh]);

  // Clear pending overrides + any stale snapshot/open panel when switching month / entity
  // so an open tally panel can never act on a previous month's rows.
  useEffect(() => { setOverrides({}); setData(null); setWhyRow(null); }, [entityId, month]);

  const approve = async () => {
    if (!entityId) return;
    if (!window.confirm(
      `Lock attendance for ${month}?\n\n` +
      `Working Days = ${workingDaysOverride || data?.default_working_days}\n` +
      `Holidays = ${holidays.length}\n` +
      `Per-employee overrides = ${Object.keys(overrides).length}\n\n` +
      `You can still unlock as long as no payroll exists for the month.`
    )) return;
    setActing(true);
    try {
      const url = new URL(`${API_URL}/api/attendance/monthly-approval/${entityId}/${month}/approve`);
      if (workingDaysOverride) url.searchParams.set('working_days', workingDaysOverride);
      const body = {
        overrides: Object.values(overrides).map(o => ({ ...o })),
      };
      const res = await fetch(url, {
        method: 'POST',
        headers: headers(),
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to approve');
      toast.success(d.message);
      setOverrides({});
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActing(false);
    }
  };

  const unlock = async () => {
    if (!entityId) return;
    if (!window.confirm(`Unlock attendance for ${month}? You will need to re-approve before generating payroll.`)) return;
    setActing(true);
    try {
      const res = await fetch(`${API_URL}/api/attendance/monthly-approval/${entityId}/${month}/unlock`,
        { method: 'POST', headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to unlock');
      toast.success(d.message);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActing(false);
    }
  };

  const recalc = async () => {
    if (!entityId) return;
    if (!window.confirm(`Rebuild ${month} from live attendance? This CLEARS all manual per-employee edits and fixes wrong values (e.g. present days > days in month).`)) return;
    setActing(true);
    try {
      const res = await fetch(`${API_URL}/api/attendance/monthly-approval/${entityId}/${month}/recalculate`,
        { method: 'POST', headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed to recalculate');
      toast.success(d.message);
      refresh();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActing(false);
    }
  };

  const saveLatePolicy = async () => {
    if (!entityId) return;
    try {
      const res = await fetch(`${API_URL}/api/attendance/late-mark-policy/${entityId}?ratio=${lateRatio}`,
        { method: 'PUT', headers: headers(), credentials: 'include' });
      const d = await safeJson(res);
      if (!res.ok) throw new Error(d.detail || 'Failed');
      toast.success(d.message);
      refresh();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const onEditSave = (override) => {
    setOverrides(prev => ({ ...prev, [override.employee_id]: override }));
    setEditingRow(null);
    toast.success('Override saved — click Lock & Approve to commit.');
  };

  const onClearOverride = (employee_id) => {
    setOverrides(prev => {
      const cp = { ...prev };
      delete cp[employee_id];
      return cp;
    });
  };

  const locked = data?.locked && data.locked.locked;
  const baseRows = locked ? (data?.locked?.employees_snapshot || []) : (data?.preview || []);

  // Apply local pending overrides on top so the preview reflects unsaved edits.
  // Mirrors the backend: absent is the balancing figure, payable = month − LWP − absent − late.
  const rows = useMemo(() => {
    return baseRows.map(r => {
      const ov = overrides[r.employee_id];
      if (!ov) return r;
      const md = Number(r.served_days ?? r.month_days ?? data?.month_days ?? r.working_days ?? 30);
      const wd = Number(ov.working_days ?? r.working_days);
      const p = Number(ov.present_days ?? r.present_days);
      const l = Number(ov.approved_leave_days ?? r.approved_leave_days);
      const u = Number(ov.lwp_days ?? r.lwp_days ?? 0);
      const lm = Number(ov.late_marks ?? r.late_marks);
      const ph = Number(ov.paid_holidays ?? r.paid_holidays);
      const wo = Number(ov.week_off_days ?? r.week_off_days ?? 0);
      const ld = Math.floor(lm / Math.max(1, lateRatio));
      const ab = Math.max(0, Math.round((md - p - l - u - ph - wo) * 10) / 10);
      const acc = Math.round((p + l + u + ph + wo + ab) * 10) / 10;
      const pay = Math.max(0, Math.round((md - u - ab - ld) * 10) / 10);
      const reasons = [];
      if (acc !== md) reasons.push(`The day counts add up to ${acc}, but this month has ${md} days.`);
      if (ab > 0) reasons.push(`${ab} day(s) have no attendance, no approved leave, and are neither a declared holiday nor a weekly off.`);
      if (ld) reasons.push(`${lm} late marks → ${ld} day deducted (every ${lateRatio} late marks = 1 day).`);
      return {
        ...r,
        month_days: md,
        working_days: wd,
        present_days: p,
        approved_leave_days: l,
        lwp_days: u,
        late_marks: lm,
        paid_holidays: ph,
        week_off_days: wo,
        late_deduction: ld,
        absent_days: ab,
        accounted_days: acc,
        payable_days: pay,
        unapproved_absent_days: ab,
        tally_ok: acc === md && ab === 0,
        tally_equation: `${md} days = ${p} present + ${l} paid leave + ${u} LWP + ${ph} holiday + ${wo} weekly off + ${ab} absent`,
        payable_equation: `${md} days − ${u} LWP − ${ab} absent${ld ? ` − ${ld} late` : ''} = ${pay} net payable days`,
        mismatch_reasons: reasons,
        fix_steps: r.fix_steps || [],
        has_override: true,
      };
    });
  }, [baseRows, overrides, lateRatio, data]);

  const departments = useMemo(() => {
    const s = new Set(rows.map(r => r.department).filter(Boolean));
    return Array.from(s).sort();
  }, [rows]);

  const filteredRows = useMemo(() => {
    return rows.filter(r =>
      (filterDept === 'all' || r.department === filterDept)
      && (filterType === 'all' || (r.salary_type || 'breakdown') === filterType)
    );
  }, [rows, filterDept, filterType]);

  return (
    <div className="space-y-4" data-testid="monthly-attendance-approval">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="w-4 h-4 text-[#DC2626]" />
            Monthly Attendance Approval — Payroll Gateway
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Month</Label>
              <Input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                data-testid="ma-month-picker"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Working Days (override)</Label>
              <Input
                type="number"
                min="1"
                max="31"
                value={workingDaysOverride}
                onChange={(e) => setWorkingDaysOverride(e.target.value)}
                placeholder={data?.default_working_days ? `default ${data.default_working_days}` : 'auto'}
                data-testid="ma-working-days"
                disabled={locked}
              />
              <p className="text-[10px] text-slate-500">
                Default = Month Days ({data?.month_days || '—'}) − Weekly Offs ({data?.week_off_count ?? '—'}) − Holidays ({data?.holiday_count ?? 0}) = {data?.default_working_days || '—'}
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Late Marks Policy</Label>
              <div className="flex gap-1">
                <Input
                  type="number"
                  min="1"
                  value={lateRatio}
                  onChange={(e) => setLateRatio(e.target.value)}
                  data-testid="ma-late-ratio"
                />
                <Button variant="outline" size="sm" onClick={saveLatePolicy} disabled={locked} data-testid="ma-late-save">
                  Save
                </Button>
              </div>
              <p className="text-[10px] text-slate-500">Every {lateRatio} late marks = 1 day deduction.</p>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <Button variant="outline" onClick={refresh} disabled={loading || acting} data-testid="ma-refresh-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Refresh'}
              </Button>
              <Button variant="outline" onClick={recalc}
                disabled={loading || acting || !entityId || (data?.payroll_already_generated || 0) > 0}
                className="border-emerald-400 text-emerald-700 hover:bg-emerald-50"
                title={!entityId ? 'Pick an institute first' : 'Rebuild from live attendance — clears manual edits, fixes wrong present-day counts'}
                data-testid="ma-recalc-btn">
                Recalculate
              </Button>
              {!locked ? (
                <Button
                  onClick={approve}
                  disabled={loading || acting || !rows.length}
                  className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                  data-testid="ma-approve-btn"
                >
                  {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />}
                  Lock & Approve
                </Button>
              ) : (
                <Button
                  onClick={unlock}
                  disabled={loading || acting || (data?.payroll_already_generated || 0) > 0}
                  variant="outline"
                  className="border-amber-300 text-amber-700 hover:bg-amber-50"
                  data-testid="ma-unlock-btn"
                >
                  {acting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Unlock className="w-4 h-4 mr-1" />}
                  Unlock
                </Button>
              )}
            </div>
          </div>

          {data && (
            <div
              className={`flex items-start gap-3 p-3 rounded-lg border ${locked ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}
              data-testid="ma-status-banner"
            >
              {locked ? <ShieldCheck className="w-5 h-5 text-green-600 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5" />}
              <div className="text-sm">
                {locked ? (
                  <>
                    <p className="font-semibold text-green-800">Attendance Locked for {month}</p>
                    <p className="text-xs text-green-700 mt-0.5">
                      {data.locked.employees_snapshot?.length || 0} employees · Working Days {data.locked.working_days} · {holidays.length} holiday(s) · Approved {data.locked.approved_at?.slice(0, 10)}
                    </p>
                    {(data.payroll_already_generated || 0) > 0 && (
                      <p className="text-xs text-blue-700 mt-1">{data.payroll_already_generated} payroll record(s) generated — Unlock disabled.</p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-amber-800">Pending HR Approval for {month}</p>
                    <p className="text-xs text-amber-700 mt-0.5">
                      Mark approved holidays on the calendar, edit individual employees if needed, then click <strong>Lock &amp; Approve</strong>.
                    </p>
                    {Object.keys(overrides).length > 0 && (
                      <p className="text-xs text-amber-700 mt-1">{Object.keys(overrides).length} pending override(s) — will be saved on Lock &amp; Approve.</p>
                    )}
                  </>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <MonthTallySummary data={data} month={month} />

      <WeeklyOffPolicyCard
        entityId={entityId}
        policy={data?.weekly_off_policy}
        disabled={locked}
        onSaved={refresh}
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <WeeklyOffCalendar
          entityId={entityId}
          month={month}
          data={data}
          onChange={refresh}
          disabled={locked}
        />
        <HolidayCalendar
          entityId={entityId}
          month={month}
          holidays={holidays.filter(h => !['weekly_off', 'working_day'].includes(h.holiday_type))}
          weekOffDates={data?.week_off_dates || []}
          onChange={refresh}
          disabled={locked}
        />
      </div>

      <Card>
        <CardHeader className="pb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between space-y-0">
          <CardTitle className="text-sm flex items-center gap-2">
            <Users className="w-4 h-4" />
            {locked ? 'Locked Snapshot' : 'Live Preview'} ({filteredRows.length} of {rows.length})
          </CardTitle>
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="w-3 h-3 text-slate-400" />
            <Select value={filterDept} onValueChange={setFilterDept}>
              <SelectTrigger className="h-8 w-36 sm:w-44" data-testid="ma-filter-department">
                <SelectValue placeholder="All departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="h-8 w-32 sm:w-40" data-testid="ma-filter-type">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="breakdown">Breakdown</SelectItem>
                <SelectItem value="consolidated">Consolidated</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {loading ? (
            <div className="text-center py-8 text-slate-500 text-sm" data-testid="ma-loading">
              <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
            </div>
          ) : !filteredRows.length ? (
            <div className="text-center py-8 text-slate-500 text-sm" data-testid="ma-empty">
              No employees match the filters. Try changing the filter or pick an Institute/Branch.
            </div>
          ) : (
            <>
            {filteredRows.some(r => r.tally_ok === false) && (
              <div className="mb-3 rounded-md border border-red-300 bg-red-50 p-2.5 text-xs text-red-900"
                data-testid="tally-mismatch-notice">
                <strong>{filteredRows.filter(r => r.tally_ok === false).length}</strong>{' '}
                {filteredRows.filter(r => r.tally_ok === false).length === 1 ? 'staff member does' : 'staff members do'} not
                tally to {data?.month_days || '—'} days for {month} — their net payable days are reduced.
                Click the red <strong>payable days</strong> number on those rows to see the exact dates, the reason,
                and one-click fixes (Mark Holiday / Weekly Off / LWP).
              </div>
            )}
            <Table className="min-w-[1000px]" data-testid="ma-snapshot-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Employee</TableHead>
                  <TableHead>Designation</TableHead>
                  <TableHead className="text-center">Type</TableHead>
                  <TableHead className="text-center">Month Days</TableHead>
                  <TableHead className="text-center">Present</TableHead>
                  <TableHead className="text-center">Leaves</TableHead>
                  <TableHead className="text-center">LWP</TableHead>
                  <TableHead className="text-center">Week Off</TableHead>
                  <TableHead className="text-center">Holidays</TableHead>
                  <TableHead className="text-center">Absent</TableHead>
                  <TableHead className="text-center">Late (deduct)</TableHead>
                  <TableHead className="text-center">Net Payable</TableHead>
                  {!locked && <TableHead className="text-center">Edit</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredRows.map(r => (
                  <TableRow key={r.employee_id} data-testid={`ma-row-${r.employee_id}`} className={r.has_override ? 'bg-amber-50/50' : ''}>
                    <TableCell>
                      <div className="text-sm font-medium flex items-center gap-1">
                        {r.employee_name || r.employee_id}
                        {r.has_override && (
                          <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700">edited</Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500">{r.department || '—'}</div>
                    </TableCell>
                    <TableCell className="text-xs">{r.designation || '—'}</TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={`text-[10px] ${r.salary_type === 'consolidated' ? 'border-amber-300 text-amber-700' : 'border-blue-300 text-blue-700'}`}>
                        {r.salary_type === 'consolidated' ? 'Cons' : 'Brk'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center text-sm">{r.month_days ?? r.working_days}</TableCell>
                    <TableCell className="text-center text-sm font-semibold text-green-700">{r.present_days}</TableCell>
                    <TableCell className="text-center text-sm">{r.approved_leave_days}</TableCell>
                    <TableCell className="text-center text-sm" data-testid={`lwp-days-${r.employee_id}`}>
                      {r.lwp_days > 0 ? (
                        <Badge className="bg-red-100 text-red-700 text-[10px]">{r.lwp_days}</Badge>
                      ) : <span className="text-slate-400">0</span>}
                    </TableCell>
                    <TableCell className="text-center text-sm text-slate-500" data-testid={`weekoff-days-${r.employee_id}`}>
                      {r.week_off_days ?? 0}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {r.paid_holidays > 0 ? (
                        <Badge className="bg-blue-100 text-blue-700 text-[10px]">+{r.paid_holidays}</Badge>
                      ) : '0'}
                    </TableCell>
                    <TableCell className="text-center text-sm" data-testid={`absent-days-${r.employee_id}`}>
                      {r.absent_days > 0 ? (
                        <Badge className="bg-rose-200 text-rose-900 text-[10px]">{r.absent_days}</Badge>
                      ) : <span className="text-slate-400">0</span>}
                    </TableCell>
                    <TableCell className="text-center text-sm">
                      {r.late_marks > 0 ? (
                        <Badge className="bg-red-100 text-red-700 text-[10px]">{r.late_marks} (-{r.late_deduction || 0})</Badge>
                      ) : <span className="text-slate-400">0</span>}
                    </TableCell>
                    <TableCell className="text-center text-sm font-bold text-[#DC2626]">
                      <button type="button" className="underline decoration-dotted"
                        onClick={() => setWhyRow(whyRow === r.employee_id ? null : r.employee_id)}
                        title="Click to see the full day tally" data-testid={`why-btn-${r.employee_id}`}>
                        {r.payable_days}
                      </button>
                      {r.tally_ok === false && (
                        <Badge className="ml-1 bg-red-100 text-red-800 text-[9px]" data-testid={`mismatch-badge-${r.employee_id}`}>!</Badge>
                      )}
                    </TableCell>
                    {!locked && (
                      <TableCell className="text-center">
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="ghost" className="h-6 w-6 p-0"
                            onClick={() => setEditingRow(r)} data-testid={`ma-edit-${r.employee_id}`}>
                            <Pencil className="w-3 h-3" />
                          </Button>
                          {r.has_override && (
                            <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-red-600"
                              onClick={() => onClearOverride(r.employee_id)}
                              data-testid={`ma-clear-${r.employee_id}`}
                              title="Clear override">
                              <X className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </>
          )}
        </CardContent>
      </Card>

      {whyRow && filteredRows.filter(r => r.employee_id === whyRow).map(r => (
        <Card key={`tally-${r.employee_id}`} className="border-slate-300 bg-slate-50">
          <CardContent className="p-4 text-xs" data-testid={`why-panel-${r.employee_id}`}>
            <PayableTallyPanel
              row={r}
              entityId={entityId}
              month={data?.month || month}
              locked={locked}
              onChanged={refresh}
            />
          </CardContent>
        </Card>
      ))}

      {locked && (
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4 flex items-start gap-3">
            <FileText className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm">
              <p className="font-semibold text-blue-900">Next step → Generate Payroll</p>
              <p className="text-xs text-blue-700 mt-1">
                Open <strong>Payroll</strong> and click <em>Generate Bulk Payroll</em> to create salary records using this locked snapshot.
                HR can still edit individual values (Advance, Local Mart, etc.) before final approval.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {editingRow && (
        <EditEmployeeDialog
          row={editingRow}
          onClose={() => setEditingRow(null)}
          onSave={onEditSave}
        />
      )}
    </div>
  );
}

export default MonthlyAttendanceApproval;
